import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Map,
  History,
  List,
  Users,
  Settings,
  LogOut,
  TrendingUp,
  TrendingDown,
  Minus,
  MapPin,
  Target,
  Flame,
  ChevronRight,
  Menu,
  X as CloseIcon,
  BarChart3,
  Lightbulb,
} from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";

// Suggestion marker icon
const suggestionIcon = L.divIcon({
  className: "custom-suggestion-icon",
  html: `<div style="
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
  "><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M12 2v20M2 12h20"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: BarChart3, label: "Inteligência", path: "/inteligencia", active: true },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function ExpansionIntelligence() {
  const { logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(null);
  const [trends, setTrends] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    loadData();
  }, [selectedPeriod]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rankingRes, trendsRes, suggestionsRes] = await Promise.all([
        api.get(`/analytics/demand-ranking?days=${selectedPeriod}`),
        api.get("/analytics/demand-trends"),
        api.get("/analytics/cto-suggestion"),
      ]);
      
      setRanking(rankingRes.data);
      setTrends(trendsRes.data);
      setSuggestions(suggestionsRes.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.error("Erro ao carregar dados de analytics");
    } finally {
      setLoading(false);
    }
  };

  // Calculate map center from suggestions
  const mapCenter = suggestions?.sugestoes?.[0]
    ? [suggestions.sugestoes[0].latitude, suggestions.sugestoes[0].longitude]
    : [-22.77, -47.41];

  const getTrendIcon = (growth) => {
    if (growth > 10) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (growth < -10) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getFlameEmoji = (index) => {
    if (index === 0) return "🔥";
    if (index === 1) return "🔥";
    if (index === 2) return "🔥";
    return "📍";
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="expansion-intelligence">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <img src="/tecplus-logo.png" alt="Tecplus" className="h-10 object-contain" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white"
          >
            {sidebarOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-slate-800">
          <img src="/tecplus-logo.png" alt="Tecplus" className="h-12 object-contain" />
          <p className="text-xs text-slate-400 mt-2">NOC Admin</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || item.active;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? "bg-[#FF4500] text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
              <Lightbulb className="w-8 h-8 text-yellow-500" />
              Inteligência de Expansão
            </h1>
            <p className="text-slate-400 mt-1">
              Análise de demanda para decisões estratégicas de expansão da rede
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF4500]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Trend Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {trends?.periodos.map((periodo, index) => (
                  <Card key={index} className="bg-slate-900 border-slate-800">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">{periodo.label}</p>
                          <p className="text-3xl font-bold text-white mt-1">{periodo.demanda}</p>
                          <p className="text-xs text-slate-500 mt-1">consultas sem cobertura</p>
                        </div>
                        {periodo.crescimento !== null && (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                            periodo.crescimento > 0 
                              ? "bg-green-500/20 text-green-400" 
                              : periodo.crescimento < 0
                              ? "bg-red-500/20 text-red-400"
                              : "bg-slate-700 text-slate-400"
                          }`}>
                            {getTrendIcon(periodo.crescimento)}
                            <span className="text-sm font-medium">
                              {periodo.crescimento > 0 ? "+" : ""}{periodo.crescimento}%
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ranking de Bairros */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-500" />
                        Ranking de Demanda por Bairro
                      </CardTitle>
                      <div className="flex gap-2">
                        {[30, 60, 90].map((days) => (
                          <Button
                            key={days}
                            variant={selectedPeriod === days ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedPeriod(days)}
                            className={selectedPeriod === days 
                              ? "bg-[#FF4500] hover:bg-[#FF4500]/90" 
                              : "border-slate-700 text-slate-400 hover:text-white"
                            }
                          >
                            {days}d
                          </Button>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-slate-400">
                      {ranking?.total_demanda || 0} consultas sem cobertura nos últimos {selectedPeriod} dias
                    </p>
                  </CardHeader>
                  <CardContent className="max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                      {ranking?.ranking?.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{getFlameEmoji(index)}</span>
                            <div>
                              <p className="font-medium text-white">{item.bairro}</p>
                              <p className="text-xs text-slate-400">
                                {item.latitude ? `${item.latitude.toFixed(4)}, ${item.longitude.toFixed(4)}` : "Sem coordenadas"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[#FF4500]">{item.demanda}</p>
                            <p className="text-xs text-slate-400">consultas</p>
                          </div>
                        </div>
                      ))}
                      {(!ranking?.ranking || ranking.ranking.length === 0) && (
                        <p className="text-center text-slate-400 py-8">
                          Nenhuma demanda registrada neste período
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Sugestões de CTO */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-500" />
                      Onde Instalar Novas CTOs
                    </CardTitle>
                    <p className="text-sm text-slate-400">
                      {suggestions?.total_demanda_sem_cobertura || 0} clientes aguardando cobertura
                    </p>
                  </CardHeader>
                  <CardContent className="max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {suggestions?.sugestoes?.map((sugestao, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {sugestao.prioridade}
                              </span>
                              <div>
                                <p className="font-medium text-green-400">
                                  Ponto Sugerido #{sugestao.prioridade}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {sugestao.latitude}, {sugestao.longitude}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-400">
                                {sugestao.clientes_potenciais}
                              </p>
                              <p className="text-xs text-slate-400">clientes</p>
                            </div>
                          </div>
                          <div className="mt-3 p-2 bg-slate-900/50 rounded text-sm text-slate-300">
                            💡 {sugestao.recomendacao}
                          </div>
                          {sugestao.enderecos_exemplo?.length > 0 && (
                            <div className="mt-2 text-xs text-slate-400">
                              Exemplos: {sugestao.enderecos_exemplo.slice(0, 2).join(" • ")}
                            </div>
                          )}
                        </div>
                      ))}
                      {(!suggestions?.sugestoes || suggestions.sugestoes.length === 0) && (
                        <p className="text-center text-slate-400 py-8">
                          Não há dados suficientes para sugestões
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Mapa de Sugestões */}
              {suggestions?.sugestoes?.length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#FF4500]" />
                      Mapa de Sugestões de Expansão
                    </CardTitle>
                    <p className="text-sm text-slate-400">
                      Pontos verdes indicam locais sugeridos para novas CTOs
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-96 rounded-lg overflow-hidden">
                      <MapContainer
                        center={mapCenter}
                        zoom={12}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; OpenStreetMap'
                        />
                        
                        {/* Áreas de demanda */}
                        {ranking?.ranking?.map((bairro, index) => (
                          bairro.latitude && (
                            <CircleMarker
                              key={`demand-${index}`}
                              center={[bairro.latitude, bairro.longitude]}
                              radius={Math.min(bairro.demanda * 3, 30)}
                              pathOptions={{
                                color: "#FF4500",
                                fillColor: "#FF4500",
                                fillOpacity: 0.3,
                              }}
                            >
                              <Popup>
                                <div className="text-center">
                                  <p className="font-bold">{bairro.bairro}</p>
                                  <p className="text-sm">{bairro.demanda} consultas</p>
                                </div>
                              </Popup>
                            </CircleMarker>
                          )
                        ))}
                        
                        {/* Sugestões de CTO */}
                        {suggestions?.sugestoes?.map((sugestao, index) => (
                          <Marker
                            key={`suggestion-${index}`}
                            position={[sugestao.latitude, sugestao.longitude]}
                            icon={suggestionIcon}
                          >
                            <Popup>
                              <div className="text-center p-2">
                                <p className="font-bold text-green-600">
                                  Sugestão #{sugestao.prioridade}
                                </p>
                                <p className="text-lg font-bold">
                                  {sugestao.clientes_potenciais} clientes
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {sugestao.recomendacao}
                                </p>
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
